import { v7 as uuidv7 } from 'uuid'
import { Logger } from '../../../components/Logger.ts'

export type PlexConnectionFactory = {
  key: string
  buildUrl: () => Promise<string>
}

export type PlexConnectionStatus = {
  endpoint: string
  readyState: number
  readyStateText: string
}

export type PlexConnectionHealth = {
  subscriptionCount: number
  connectedSocketCount: number
  readyState: number
  readyStateText: string
}

type ConnectionHealthListener = (health: PlexConnectionHealth) => void

export type PlexSubscription<T = unknown> = {
  key: string
  path: string
  subscribeData: Record<string, unknown>
  unsubscribeData: Record<string, unknown>
  onNotification: (data: T) => void
  onSubscribed?: () => void
  onDisconnected?: (event: CloseEvent) => void
  onError?: (error: Error) => void
  onReconnect?: () => void | Promise<void>
}

type PlexEnvelope = {
  id?: string
  kind?: 'response' | 'notification'
  path?: string
  data?: unknown
  error?: string
}

type PendingRequest = {
  path: string
  resolve: (data: unknown) => void
  reject: (error: Error) => void
  timeout: ReturnType<typeof setTimeout>
}

type SubscriptionEntry = Omit<
  PlexSubscription,
  'onNotification' | 'onSubscribed' | 'onDisconnected' | 'onError' | 'onReconnect'
> & {
  handlers: Set<(data: unknown) => void>
  subscribedHandlers: Set<() => void>
  disconnectedHandlers: Set<(event: CloseEvent) => void>
  errorHandlers: Set<(error: Error) => void>
  reconnectHandlers: Set<() => void | Promise<void>>
  activatedGeneration?: number
}

const REQUEST_TIMEOUT_MS = 15_000
const CLOSE_NO_SUBSCRIPTIONS_DELAY_MS = 1_000
const MAX_RECONNECT_DELAY_MS = 30_000

export class PlexWebSocketClient {
  private socket?: WebSocket
  private factory?: PlexConnectionFactory
  private connectPromise?: Promise<void>
  private connectResolve?: () => void
  private connectReject?: (error: Error) => void
  private pending = new Map<string, PendingRequest>()
  private subscriptions = new Map<string, SubscriptionEntry>()
  private reconnectTimer?: ReturnType<typeof setTimeout>
  private idleCloseTimer?: ReturnType<typeof setTimeout>
  private reconnectAttempt = 0
  private successfulConnections = 0
  private shouldReconnectAfterClose = false
  private connectionHealthListeners = new Set<ConnectionHealthListener>()
  private readonly reconnectBaseDelayMs: number

  constructor(reconnectBaseDelayMs = 1_000) {
    this.reconnectBaseDelayMs = reconnectBaseDelayMs
  }

  configure(factory: PlexConnectionFactory) {
    const changed = this.factory?.key !== factory.key
    this.factory = factory
    if (!changed) return

    this.reconnectAttempt = 0
    if (this.socket) {
      this.shouldReconnectAfterClose = this.subscriptions.size > 0
      this.socket.close(1000, 'Authentication context changed')
      this.emitConnectionHealth()
    } else if (this.subscriptions.size > 0) {
      void this.ensureConnected().catch((error) => Logger.warn('wsplex: connect failed', error))
    }
  }

  subscribe<T>(subscription: PlexSubscription<T>): () => void {
    this.clearIdleClose()
    const handler = subscription.onNotification as (data: unknown) => void
    const existing = this.subscriptions.get(subscription.key)

    // Consumers with the same key share one server subscription and retain separate callbacks.
    if (existing) {
      if (existing.path !== subscription.path) {
        throw new Error(
          `wsplex subscription key ${subscription.key} is already used by ${existing.path}`,
        )
      }
      existing.handlers.add(handler)
      if (subscription.onSubscribed) existing.subscribedHandlers.add(subscription.onSubscribed)
      if (subscription.onDisconnected) {
        existing.disconnectedHandlers.add(subscription.onDisconnected)
      }
      if (subscription.onError) existing.errorHandlers.add(subscription.onError)
      if (subscription.onReconnect) existing.reconnectHandlers.add(subscription.onReconnect)
    } else {
      const entry: SubscriptionEntry = {
        key: subscription.key,
        path: subscription.path,
        subscribeData: subscription.subscribeData,
        unsubscribeData: subscription.unsubscribeData,
        handlers: new Set([handler]),
        subscribedHandlers: new Set(subscription.onSubscribed ? [subscription.onSubscribed] : []),
        disconnectedHandlers: new Set(
          subscription.onDisconnected ? [subscription.onDisconnected] : [],
        ),
        errorHandlers: new Set(subscription.onError ? [subscription.onError] : []),
        reconnectHandlers: new Set(subscription.onReconnect ? [subscription.onReconnect] : []),
      }
      this.subscriptions.set(subscription.key, entry)
      this.emitConnectionHealth()
      if (this.socket?.readyState === WebSocket.OPEN) void this.activate(entry, false)
      else {
        void this.ensureConnected().catch((error) => Logger.warn('wsplex: connect failed', error))
      }
    }

    let disposed = false
    return () => {
      if (disposed) return
      disposed = true
      const current = this.subscriptions.get(subscription.key)
      if (!current) return

      current.handlers.delete(handler)
      if (subscription.onSubscribed) current.subscribedHandlers.delete(subscription.onSubscribed)
      if (subscription.onDisconnected) {
        current.disconnectedHandlers.delete(subscription.onDisconnected)
      }
      if (subscription.onError) current.errorHandlers.delete(subscription.onError)
      if (subscription.onReconnect) current.reconnectHandlers.delete(subscription.onReconnect)
      // Unsubscribe on the server only after the last consumer releases this key.
      if (current.handlers.size > 0) return

      this.subscriptions.delete(subscription.key)
      this.emitConnectionHealth()
      if (this.socket?.readyState === WebSocket.OPEN) {
        void this.request(current.path, current.unsubscribeData).catch((error) => {
          Logger.warn(`wsplex: unsubscribe failed for ${current.key}`, error)
        })
      }
      this.scheduleIdleClose()
    }
  }

  async request<T = unknown>(path: string, data: Record<string, unknown>): Promise<T> {
    await this.ensureConnected()
    const socket = this.socket
    if (!socket || socket.readyState !== WebSocket.OPEN)
      throw new Error('wsplex: socket is not open')

    const id = uuidv7()
    return new Promise<T>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pending.delete(id)
        reject(new Error(`wsplex: request timed out for ${path}`))
      }, REQUEST_TIMEOUT_MS)

      this.pending.set(id, {
        path,
        resolve: resolve as (data: unknown) => void,
        reject,
        timeout,
      })

      try {
        socket.send(JSON.stringify({ id, path, data }))
      } catch (error) {
        clearTimeout(timeout)
        this.pending.delete(id)
        reject(error instanceof Error ? error : new Error(String(error)))
      }
    })
  }

  shutdown() {
    this.clearReconnect()
    this.clearIdleClose()
    this.shouldReconnectAfterClose = false
    this.subscriptions.clear()
    this.rejectPending(new Error('wsplex: client shut down'))
    if (this.socket && this.socket.readyState < WebSocket.CLOSING) {
      this.socket.close(1000, 'Client shut down')
    }
    this.socket = undefined
    this.factory = undefined
    this.successfulConnections = 0
    this.resetConnectPromise()
    this.emitConnectionHealth()
  }

  getSubscriptionCount(): number {
    return this.subscriptions.size
  }

  getActiveOrConnectingCount(): number {
    if (this.subscriptions.size === 0) return 0
    const readyState = this.socket?.readyState
    return readyState === WebSocket.CONNECTING || readyState === WebSocket.OPEN ? 1 : 0
  }

  getConnectedSocketCount(): number {
    return this.socket?.readyState === WebSocket.OPEN ? 1 : 0
  }

  getConnectionHealth(): PlexConnectionHealth {
    const readyState = this.socket?.readyState ?? WebSocket.CLOSED
    return {
      subscriptionCount: this.subscriptions.size,
      connectedSocketCount: this.getConnectedSocketCount(),
      readyState,
      readyStateText: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][readyState] ?? 'UNKNOWN',
    }
  }

  subscribeToConnectionHealth(listener: ConnectionHealthListener): () => void {
    this.connectionHealthListeners.add(listener)
    listener(this.getConnectionHealth())
    return () => this.connectionHealthListeners.delete(listener)
  }

  getConnectionStatus(): PlexConnectionStatus[] {
    if (this.subscriptions.size === 0) return []
    const readyState = this.socket?.readyState ?? WebSocket.CLOSED
    const readyStateText = ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][readyState] ?? 'UNKNOWN'
    return [
      {
        endpoint: '/plex',
        readyState,
        readyStateText,
      },
    ]
  }

  private async ensureConnected(): Promise<void> {
    if (this.socket?.readyState === WebSocket.OPEN) return
    // Concurrent requests must await the same handshake rather than open competing sockets.
    if (this.connectPromise) return this.connectPromise
    if (!this.factory) throw new Error('wsplex: connection factory is not configured')

    this.clearReconnect()
    this.connectPromise = new Promise<void>((resolve, reject) => {
      this.connectResolve = resolve
      this.connectReject = reject
    })
    const connectPromise = this.connectPromise

    try {
      const url = await this.factory.buildUrl()
      const socket = new WebSocket(url)
      this.socket = socket
      this.emitConnectionHealth()

      socket.onopen = () => {
        if (this.socket !== socket) return
        const isReconnect = this.successfulConnections > 0
        this.successfulConnections += 1
        this.reconnectAttempt = 0
        this.connectResolve?.()
        this.resetConnectPromise()
        this.emitConnectionHealth()
        void this.restoreSubscriptions(isReconnect)
      }
      socket.onmessage = (event) => {
        if (this.socket === socket) this.handleMessage(event.data)
      }
      socket.onerror = () => {
        if (this.socket !== socket) return
        const error = new Error('wsplex: socket error')
        for (const entry of this.subscriptions.values()) {
          for (const handler of entry.errorHandlers) handler(error)
        }
        this.connectReject?.(error)
        this.resetConnectPromise()
        this.emitConnectionHealth()
      }
      socket.onclose = (event) => {
        if (this.socket !== socket) return
        this.socket = undefined
        const error = new Error(
          `wsplex: socket closed (${event.code}${event.reason ? `: ${event.reason}` : ''})`,
        )
        this.connectReject?.(error)
        this.resetConnectPromise()
        this.rejectPending(error)
        this.emitConnectionHealth()
        for (const entry of this.subscriptions.values()) {
          for (const handler of entry.disconnectedHandlers) handler(event)
        }

        const reconnect = this.shouldReconnectAfterClose || this.subscriptions.size > 0
        this.shouldReconnectAfterClose = false
        if (reconnect) this.scheduleReconnect()
      }
    } catch (error) {
      const connectionError = error instanceof Error ? error : new Error(String(error))
      this.connectReject?.(connectionError)
      this.resetConnectPromise()
      if (this.subscriptions.size > 0) this.scheduleReconnect()
    }

    // Return the same promise even when setup failed and cleared the shared field.
    return connectPromise
  }

  private handleMessage(raw: unknown) {
    let envelope: PlexEnvelope
    try {
      const text = typeof raw === 'string' ? raw : new TextDecoder().decode(raw as ArrayBuffer)
      envelope = JSON.parse(text) as PlexEnvelope
    } catch (error) {
      Logger.error('wsplex: failed to parse message', error)
      return
    }

    if (envelope.kind === 'notification') {
      if (!envelope.path) return
      for (const entry of this.subscriptions.values()) {
        if (entry.path !== envelope.path) continue
        for (const handler of entry.handlers) {
          try {
            handler(envelope.data)
          } catch (error) {
            Logger.error(`wsplex: notification handler failed for ${entry.key}`, error)
          }
        }
      }
      return
    }

    if (!envelope.id) return
    const pending = this.pending.get(envelope.id)
    if (!pending) return
    clearTimeout(pending.timeout)
    this.pending.delete(envelope.id)
    if (envelope.error) pending.reject(new Error(`wsplex: ${pending.path}: ${envelope.error}`))
    else pending.resolve(envelope.data)
  }

  private async restoreSubscriptions(isReconnect: boolean) {
    await Promise.all(
      Array.from(this.subscriptions.values(), (entry) => this.activate(entry, isReconnect)),
    )
  }

  private async activate(entry: SubscriptionEntry, isReconnect: boolean) {
    // Activation may be requested both when subscribing and when restoring a connection.
    // Send at most one subscribe request per entry for each successful connection.
    const generation = this.successfulConnections
    if (entry.activatedGeneration === generation) return
    entry.activatedGeneration = generation
    try {
      await this.request(entry.path, entry.subscribeData)
      for (const handler of entry.subscribedHandlers) handler()
      if (!isReconnect || !this.subscriptions.has(entry.key)) return
      await Promise.all(
        Array.from(entry.reconnectHandlers, (handler) => Promise.resolve(handler())),
      )
    } catch (error) {
      if (entry.activatedGeneration === generation) entry.activatedGeneration = undefined
      const requestError = error instanceof Error ? error : new Error(String(error))
      for (const handler of entry.errorHandlers) handler(requestError)
      Logger.error(`wsplex: subscription failed for ${entry.key}`, requestError)
    }
  }

  private rejectPending(error: Error) {
    for (const request of this.pending.values()) {
      clearTimeout(request.timeout)
      request.reject(error)
    }
    this.pending.clear()
  }

  private scheduleReconnect() {
    if (this.reconnectTimer || !this.factory || this.subscriptions.size === 0) return
    const delay = Math.min(
      this.reconnectBaseDelayMs * 2 ** this.reconnectAttempt,
      MAX_RECONNECT_DELAY_MS,
    )
    this.reconnectAttempt += 1
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined
      void this.ensureConnected().catch((error) => Logger.warn('wsplex: reconnect failed', error))
    }, delay)
  }

  private scheduleIdleClose() {
    if (this.subscriptions.size > 0 || this.idleCloseTimer) return
    // Allow brief unmount/remount cycles to reuse the socket before closing an idle connection.
    this.idleCloseTimer = setTimeout(() => {
      this.idleCloseTimer = undefined
      if (this.subscriptions.size > 0) return
      this.clearReconnect()
      this.shouldReconnectAfterClose = false
      if (this.socket && this.socket.readyState < WebSocket.CLOSING) {
        this.socket.close(1000, 'No active subscriptions')
        this.emitConnectionHealth()
      }
    }, CLOSE_NO_SUBSCRIPTIONS_DELAY_MS)
  }

  private clearReconnect() {
    if (!this.reconnectTimer) return
    clearTimeout(this.reconnectTimer)
    this.reconnectTimer = undefined
  }

  private clearIdleClose() {
    if (!this.idleCloseTimer) return
    clearTimeout(this.idleCloseTimer)
    this.idleCloseTimer = undefined
  }

  private resetConnectPromise() {
    this.connectPromise = undefined
    this.connectResolve = undefined
    this.connectReject = undefined
  }

  private emitConnectionHealth() {
    const health = this.getConnectionHealth()
    for (const listener of this.connectionHealthListeners) {
      try {
        listener(health)
      } catch (error) {
        Logger.error('wsplex: connection health listener failed', error)
      }
    }
  }
}

export const plexWebSocketClient = new PlexWebSocketClient()
